import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { LoadingSpinner } from '../loading-spinner';
import { voicemailService } from '../../services/voicemail-service';
import { resolveRecordingUrl } from '../../helpers/utils';
import { toast } from 'react-toastify';
import { callTypes } from '../../assets/data';
import { Trash } from '@phosphor-icons/react';
import VoicemailReports from '../../pages/voicemail-reports';

const VoicemailDashboard = () => {
  const [voicemails, setVoicemails] = useState([]);
  const [filteredVoicemails, setFilteredVoicemails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('unread');
  const [showReports, setShowReports] = useState(false);
  const [currentPlayingId, setCurrentPlayingId] = useState(null);
  const audioRef = useRef(null);
  const currentAudioUrlRef = useRef('');
  const [totalCounts, setTotalCounts] = useState({ unread: 0, read: 0, all: 0 });
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryValue, setEditingCategoryValue] = useState('');
  const isInitialLoad = useRef(true);

  useEffect(() => {
    // Reset to page 1 when tab changes
    setCurrentPage(1);
  }, [activeTab]);

  useEffect(() => {
    // Only show loading spinner on initial load, not on tab/page changes
    const showLoading = isInitialLoad.current;
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
    }
    loadVoicemails(showLoading);
  }, [currentPage, activeTab]);

  // Load total counts for all tabs on initial load and when needed
  useEffect(() => {
    loadTotalCounts();
  }, []);

  useEffect(() => {
    filterVoicemails();
  }, [voicemails, activeTab]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  // Handle Escape key to cancel category editing
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && editingCategoryId) {
        setEditingCategoryId(null);
        setEditingCategoryValue('');
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [editingCategoryId]);

  const loadTotalCounts = async () => {
    try {
      // Fetch page 1 for each tab to get total counts
      const [unreadResponse, readResponse, allResponse] = await Promise.all([
        voicemailService.fetchVoicemails(1, 'unread'),
        voicemailService.fetchVoicemails(1, 'read'),
        voicemailService.fetchVoicemails(1, 'all')
      ]);

      setTotalCounts({
        unread: unreadResponse.pagination?.total || 0,
        read: readResponse.pagination?.total || 0,
        all: allResponse.pagination?.total || 0
      });
    } catch (err) {
      console.error('Error loading total counts:', err);
      // Don't show error toast for counts, just log it
    }
  };

  const loadVoicemails = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      // Pass activeTab to the API so pagination is per tab
      const response = await voicemailService.fetchVoicemails(currentPage, activeTab);
      
      // Handle the new API response structure
      let voicemailsData = [];
      if (response.voicemails) {
        // New structure with pagination
        voicemailsData = response.voicemails;
        setPagination(response.pagination);
        
        // Update total count for the active tab
        if (response.pagination?.total !== undefined) {
          setTotalCounts(prev => ({
            ...prev,
            [activeTab]: response.pagination.total
          }));
        }
      } else if (Array.isArray(response)) {
        // Fallback for array response
        voicemailsData = response;
        setPagination(null);
      }
      
      // Format duration helper
      const formatDuration = (seconds) => {
        if (!seconds || seconds === 0) return '0:00';
        const totalSeconds = parseInt(seconds);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;
        
        if (hours > 0) {
          return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        }
        return `${minutes}:${String(secs).padStart(2, '0')}`;
      };

      // Transform and validate data based on actual API structure
      const transformedData = voicemailsData.map(voicemail => {
        const resolvedRecordingUrl = resolveRecordingUrl(
          voicemail.recording_url,
          voicemail.recording_sid,
        );
        return {
        ...voicemail,
        id: voicemail.id,
        taskSid: voicemail.task_sid,
        callSid: voicemail.call_sid,
        recordingSid: voicemail.recording_sid,
        twilioNumber: voicemail.twilio_number,
        userNumber: voicemail.user_number,
        recordingUrl: resolvedRecordingUrl,
        duration: formatDuration(voicemail.duration),
        // Handle status field - provide default if missing
        status: voicemail.status ? 
                (voicemail.status === 'processed' ? 'read' : 
                 voicemail.status === 'failed' ? 'failed' : 
                 voicemail.status === 'archived' ? 'archived' : 'unread') : 'unread',
        // Map read field (boolean) - preserve undefined to treat as unread
        read: voicemail.read === true ? true : (voicemail.read === false ? false : undefined),
        transcription: voicemail.transcription || 'No transcription available',
        // Handle email fields - provide defaults if missing
        emailSent: voicemail.email_sent !== undefined ? voicemail.email_sent : false,
        emailSentAt: voicemail.email_sent_at || null,
        notes: voicemail.notes || null,
        category: voicemail.category || null,
        timestamp: voicemail.created_at || voicemail.timestamp,
        updatedAt: voicemail.updated_at,
        // Legacy field mappings for compatibility
        callerName: voicemail.user_number ? decodeURIComponent(voicemail.user_number) : 'Unknown Number',
        callerNumber: voicemail.user_number ? decodeURIComponent(voicemail.user_number) : 'Unknown Number',
        audioUrl: resolvedRecordingUrl,
      };
      });
      
      setVoicemails(transformedData);
    } catch (err) {
      setError('Failed to load voicemails');
      toast.error('Failed to load voicemails');
      console.error('Error loading voicemails:', err);
    } finally {
      setLoading(false);
    }
  };


  const updateVoicemailStatus = async (id, status) => {
    try {
      setUpdatingStatus(true);
      // Use main endpoint - support both string status and object format
      const updateData = typeof status === 'string' 
        ? { status, read: status === 'read' } 
        : status;
      await voicemailService.updateVoicemail(id, updateData);
      
      // Update local state - set read field based on status
      const isRead = typeof status === 'string' ? status === 'read' : (status.read === true);
      const statusValue = typeof status === 'string' ? status : (status.status || 'read');
      const previousVoicemail = voicemails.find(v => v.id === id);
      const wasUnread = previousVoicemail?.read === false || previousVoicemail?.read === undefined;
      
      setVoicemails(prev => 
        prev.map(v => 
          v.id === id ? { ...v, status: statusValue, read: isRead } : v
        )
      );
      
      
      // Update total counts
      if (wasUnread && isRead) {
        // Moved from unread to read
        setTotalCounts(prev => ({
          unread: Math.max(0, prev.unread - 1),
          read: prev.read + 1,
          all: prev.all
        }));
      } else if (!wasUnread && !isRead) {
        // Moved from read to unread
        setTotalCounts(prev => ({
          unread: prev.unread + 1,
          read: Math.max(0, prev.read - 1),
          all: prev.all
        }));
      }
      
      toast.success(`Voicemail marked as ${statusValue}`);
    } catch (err) {
      toast.error('Failed to update voicemail status');
      console.error('Error updating voicemail status:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const updateVoicemailCategory = async (id, category) => {
    try {
      setUpdatingStatus(true);
      
      // Update both category and read flag via main endpoint
      await voicemailService.updateVoicemail(id, { 
        category: category || null,
        read: true 
      });
      
      // Update local state
      const updatedVoicemail = voicemails.find(v => v.id === id);
      const wasUnread = updatedVoicemail?.read === false || updatedVoicemail?.read === undefined;
      
      setVoicemails(prev => 
        prev.map(v => 
          v.id === id ? { ...v, category, status: 'read', read: true } : v
        )
      );
      
      
      // Update total counts if was unread
      if (wasUnread) {
        setTotalCounts(prev => ({
          unread: Math.max(0, prev.unread - 1),
          read: prev.read + 1,
          all: prev.all
        }));
      }
      
      toast.success('Category updated and marked as read');
    } catch (err) {
      toast.error('Failed to update category');
      console.error('Error updating category:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCategoryClick = (e, voicemail) => {
    e.stopPropagation(); // Prevent row click
    setEditingCategoryId(voicemail.id);
    setEditingCategoryValue(voicemail.category || '');
  };

  const handleCategoryAccept = async (e, voicemailId) => {
    e.stopPropagation();
    const category = editingCategoryValue || null;
    await updateVoicemailCategory(voicemailId, category); // Always marks as read
    setEditingCategoryId(null);
    setEditingCategoryValue('');
  };

  const handleCategoryCancel = (e) => {
    e.stopPropagation();
    setEditingCategoryId(null);
    setEditingCategoryValue('');
  };

  const deleteVoicemail = async (id) => {
    try {
      setUpdatingStatus(true);
      const voicemailToDelete = voicemails.find(v => v.id === id);
      const wasUnread = voicemailToDelete?.read === false || voicemailToDelete?.read === undefined;
      
      await voicemailService.deleteVoicemail(id);
      
      // Remove from local state
      setVoicemails(prev => prev.filter(v => v.id !== id));
      setFilteredVoicemails(prev => prev.filter(v => v.id !== id));
      
      
      // Update total counts
      setTotalCounts(prev => ({
        unread: wasUnread ? Math.max(0, prev.unread - 1) : prev.unread,
        read: !wasUnread ? Math.max(0, prev.read - 1) : prev.read,
        all: Math.max(0, prev.all - 1)
      }));
      
      toast.success('Voicemail deleted successfully');
    } catch (err) {
      toast.error('Failed to delete voicemail');
      console.error('Error deleting voicemail:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDeleteClick = (e, voicemail) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete this voicemail from ${voicemail.callerNumber}? This action cannot be undone.`)) {
      deleteVoicemail(voicemail.id);
    }
  };

  const filterVoicemails = () => {
    let filtered = voicemails;
    
    // Filter by tab using read field (boolean)
    // Unread tab: read is false or undefined
    // Read tab: read is true
    if (activeTab === 'unread') {
      filtered = voicemails.filter(v => v.read === false || v.read === undefined);
    } else if (activeTab === 'read') {
      filtered = voicemails.filter(v => v.read === true);
    }
    // If activeTab is 'all', show all voicemails
    
    setFilteredVoicemails(filtered);
  };

  const handleStopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentPlayingId(null);
    }
  };

  const handlePlayAudio = async (voicemail) => {
    try {
      // If clicking on the same voicemail that's playing, stop it
      if (currentPlayingId === voicemail.id && audioRef.current && !audioRef.current.paused) {
        handleStopAudio();
        return;
      }

      // Stop any currently playing audio
      handleStopAudio();

      const audioUrl = voicemail.recordingUrl || voicemail.audioUrl;

      if (audioUrl && audioUrl.trim() !== '') {
        const trimmedUrl = audioUrl.trim();
        const isValidUrl = trimmedUrl.startsWith('http://') || 
                          trimmedUrl.startsWith('https://') || 
                          trimmedUrl.startsWith('/') ||
                          trimmedUrl.startsWith('data:') ||
                          trimmedUrl.startsWith('blob:');
        
        if (!isValidUrl) {
          console.warn('Invalid audio URL format:', trimmedUrl);
          toast.error('Invalid audio URL format');
          return;
        }
        
        currentAudioUrlRef.current = trimmedUrl;
        setCurrentPlayingId(voicemail.id);
        if (audioRef.current) {
          audioRef.current.src = trimmedUrl;
          audioRef.current.play().catch(err => {
            setCurrentPlayingId(null);
            if (err.name === 'NotSupportedError') {
              console.warn('Audio not supported:', err.message);
              toast.error('Audio format not supported by browser or URL is invalid');
            } else if (err.name === 'NotAllowedError') {
              console.warn('Audio play blocked by browser:', err.message);
              toast.error('Browser prevented audio playback. Please click play again.');
            } else if (err.name === 'AbortError') {
              console.warn('Audio play aborted:', err.message);
            } else {
              console.warn('Audio play error:', err.name, err.message);
              toast.error('Unable to play audio. Please check the audio file.');
            }
          });
        }
      }
    } catch (error) {
      console.error('Error getting audio URL:', error);
      toast.error('Failed to load audio');
    }
  };

  const handleAudioEnded = () => {
    setCurrentPlayingId(null);
  };

  const handleAudioError = (e) => {
    const audioEl = e.target;
    if (audioEl?.error) {
      const errorCode = audioEl.error.code;
      if (errorCode !== 1) {
        setCurrentPlayingId(null);
        const url = currentAudioUrlRef.current;
        if (errorCode === 4 || errorCode === 2) {
          toast.error('Audio file format not supported or network error');
        } else if (errorCode === 3) {
          toast.error('Audio file is corrupted or cannot be decoded');
        } else {
          console.warn('Audio playback error:', audioEl.error);
        }
      }
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Wrapper>
        <Header>
          <HeaderText>
            <h1>
              Voicemail
            </h1>
          </HeaderText>
        </Header>
        <ErrorState>
          <p>{error}</p>
          <RetryButton onClick={loadVoicemails}>Retry</RetryButton>
        </ErrorState>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Header>
        <HeaderContent>
          <HeaderText>
            <h1>
              Voicemail
            </h1>
          </HeaderText>
          <RefreshButton onClick={() => {
            loadTotalCounts();
            loadVoicemails();
          }} disabled={loading}>
            {loading ? '⟳' : '↻'} Refresh
          </RefreshButton>
        </HeaderContent>
      </Header>

      <TabContainer>
        <TabButton 
          type="button"
          $active={activeTab === 'unread' && !showReports} 
          onClick={(e) => {
            e.preventDefault();
            setActiveTab('unread');
            setShowReports(false);
          }}
        >
          Unread ({totalCounts.unread})
        </TabButton>
        <TabButton 
          type="button"
          $active={activeTab === 'read' && !showReports} 
          onClick={(e) => {
            e.preventDefault();
            setActiveTab('read');
            setShowReports(false);
          }}
        >
          Read ({totalCounts.read})
        </TabButton>
        <TabButton 
          type="button"
          $active={activeTab === 'all' && !showReports} 
          onClick={(e) => {
            e.preventDefault();
            setActiveTab('all');
            setShowReports(false);
          }}
        >
          All ({totalCounts.all})
        </TabButton>
        <TabButton 
          type="button"
          $active={showReports} 
          onClick={(e) => {
            e.preventDefault();
            setShowReports(true);
          }}
        >
          Reports
        </TabButton>
      </TabContainer>

      <AudioPlayerBar $visible={currentPlayingId !== null}>
        <InlineAudio
          ref={audioRef}
          controls
          preload="metadata"
          onEnded={handleAudioEnded}
          onError={handleAudioError}
        />
      </AudioPlayerBar>

      <Content>
        {showReports ? (
          <ReportsContainer>
            <VoicemailReports />
          </ReportsContainer>
        ) : (
          <>
        <VoicemailTableContainer>
          <TableHeader>
            <TableTitle>Voicemail Messages ({filteredVoicemails.length})</TableTitle>
          </TableHeader>
          
          <Table>
            <TableBody>
              <TableRow style={{ backgroundColor: '#F1F1F1' }}>
                <TableCell style={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Audio File</TableCell>
                <TableCell style={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Call Number</TableCell>
                <TableCell style={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Twilio Number</TableCell>
                <TableCell style={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category</TableCell>
                <TableCell style={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date & Time</TableCell>
                <TableCell style={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Duration</TableCell>
                <TableCell style={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</TableCell>
              </TableRow>
              {filteredVoicemails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                    No voicemail messages found
                  </TableCell>
                </TableRow>
              ) : (
                filteredVoicemails.map(voicemail => (
                  <TableRow 
                    key={voicemail.id}
                    $status={voicemail.status}
                    $read={voicemail.read}
                  >
                    <TableCell>
                      <PlayButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayAudio(voicemail);
                        }}
                        disabled={!(voicemail.recordingUrl || voicemail.audioUrl)}
                        $isPlaying={currentPlayingId === voicemail.id}
                      >
                        {currentPlayingId === voicemail.id ? '⏹' : '▶'}
                      </PlayButton>
                    </TableCell>
                    <TableCell>
                      <PhoneNumber $isUnread={voicemail.read === false || voicemail.read === undefined}>
                        {voicemail.callerNumber}
                      </PhoneNumber>
                    </TableCell>
                    <TableCell>
                      <PhoneNumber>{voicemail.twilioNumber ? decodeURIComponent(voicemail.twilioNumber) : 'N/A'}</PhoneNumber>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {editingCategoryId === voicemail.id ? (
                        <CategoryEditContainer>
                          <CategoryEditSelect
                            value={editingCategoryValue}
                            onChange={(e) => setEditingCategoryValue(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          >
                            <option value="">Uncategorized</option>
                            {callTypes.map(type => (
                              <option key={type.id || type.key} value={type.type}>
                                {type.type}
                              </option>
                            ))}
                          </CategoryEditSelect>
                          <CategoryActionButton 
                            onClick={(e) => handleCategoryAccept(e, voicemail.id)}
                            $accept
                            title="Accept"
                          >
                            ✓
                          </CategoryActionButton>
                          <CategoryActionButton 
                            onClick={handleCategoryCancel}
                            $cancel
                            title="Cancel"
                          >
                            ×
                          </CategoryActionButton>
                        </CategoryEditContainer>
                      ) : (
                        <CategoryBadge 
                          $hasCategory={!!voicemail.category}
                          onClick={(e) => handleCategoryClick(e, voicemail)}
                          $editable
                        >
                          {voicemail.category || 'Uncategorized'}
                        </CategoryBadge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Timestamp>{formatTimestamp(voicemail.timestamp)}</Timestamp>
                    </TableCell>
                    <TableCell>
                      <Duration>{voicemail.duration}</Duration>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DeleteButton
                        onClick={(e) => handleDeleteClick(e, voicemail)}
                        disabled={updatingStatus}
                        title="Delete voicemail"
                      >
                        <Trash size={18} />
                      </DeleteButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </VoicemailTableContainer>

        {pagination && !showReports && (() => {
          const totalPages = pagination.last_page || Math.ceil((pagination.total || 0) / (pagination.per_page || 10));
          
          return (
            <PaginationWrapper>
              <PaginationText>
                Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0}
              </PaginationText>
              
              <PaginationControls>
                <PaginationButton 
                  onClick={() => {
                    handleStopAudio();
                    setCurrentPage(currentPage - 1);
                  }}
                  disabled={!pagination.prev_page_url || currentPage === 1}
                  $disabled={!pagination.prev_page_url || currentPage === 1}
                >
                  {"< < PREVIOUS"}
                </PaginationButton>
                
                <PageNumbers>
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    
                    if (totalPages <= maxVisiblePages) {
                      // Show all pages if total pages is less than max visible
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(
                          <PageButton
                            key={i}
                            $active={currentPage === i}
                            onClick={() => {
                              handleStopAudio();
                              setCurrentPage(i);
                            }}
                          >
                            {i}
                          </PageButton>
                        );
                      }
                    } else {
                      // Always show first page
                      pages.push(
                        <PageButton
                          key={1}
                          $active={currentPage === 1}
                          onClick={() => {
                            handleStopAudio();
                            setCurrentPage(1);
                          }}
                        >
                          1
                        </PageButton>
                      );

                      // Calculate start and end of visible pages
                      let startPage = Math.max(2, currentPage - 1);
                      let endPage = Math.min(totalPages - 1, currentPage + 1);

                      // Adjust if at the start
                      if (currentPage <= 2) {
                        endPage = 4;
                      }
                      // Adjust if at the end
                      if (currentPage >= totalPages - 1) {
                        startPage = totalPages - 3;
                      }

                      // Add ellipsis after first page if needed
                      if (startPage > 2) {
                        pages.push(<PageEllipsis key="start">...</PageEllipsis>);
                      }

                      // Add middle pages
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <PageButton
                            key={i}
                            $active={currentPage === i}
                            onClick={() => {
                              handleStopAudio();
                              setCurrentPage(i);
                            }}
                          >
                            {i}
                          </PageButton>
                        );
                      }

                      // Add ellipsis before last page if needed
                      if (endPage < totalPages - 1) {
                        pages.push(<PageEllipsis key="end">...</PageEllipsis>);
                      }

                      // Always show last page
                      pages.push(
                        <PageButton
                          key={totalPages}
                          $active={currentPage === totalPages}
                          onClick={() => {
                            handleStopAudio();
                            setCurrentPage(totalPages);
                          }}
                        >
                          {totalPages}
                        </PageButton>
                      );
                    }
                    return pages;
                  })()}
                </PageNumbers>
                
                <PaginationButton 
                  onClick={() => {
                    handleStopAudio();
                    setCurrentPage(currentPage + 1);
                  }}
                  disabled={!pagination.next_page_url}
                  $disabled={!pagination.next_page_url}
                >
                  {"NEXT > >"}
                </PaginationButton>
              </PaginationControls>
            </PaginationWrapper>
          );
        })()}
          </>
        )}
      </Content>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  padding: 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

const Header = styled.div`
  margin-bottom: 32px;
  padding: 32px 0;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderText = styled.div`
  h1 {
    color: #EC3310;
    font-size: 2.75rem;
    font-weight: 700;
    margin-bottom: 8px;
    letter-spacing: -0.02em;
    display: flex;
    align-items: center;
    gap: 16px;
  }
  
  p {
    color: #6b7280;
    font-size: 1.125rem;
    font-weight: 400;
    margin: 0;
  }
`;


const RefreshButton = styled.button`
  background: #2DB3C0;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(45, 179, 192, 0.3);
  
  &:hover:not(:disabled) {
    background: #26a0ac;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(45, 179, 192, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: calc(100vh - 200px);
`;

const ReportsContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const VoicemailTableContainer = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow-x: auto;
  overflow-y: auto;
  max-height: 600px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Table = styled.table`
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
  background: white;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid #f1f5f9;
  
  &:hover {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
  
  
`;


const TableCell = styled.td`
  padding: 16px;
  vertical-align: middle;
  font-size: 0.875rem;
  color: #178B96;
`;

const StatusIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  ${props => props.$status === 'read' && `
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
  `}
  
  ${props => props.$status === 'failed' && `
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
  `}
  
  ${props => props.$status === 'unread' && `
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    color: white;
  `}
  
  ${props => props.$status === 'archived' && `
    background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
    color: white;
  `}
`;

const PhoneNumber = styled.div`
  color: #178B96;
  font-size: 0.875rem;
  font-weight: ${props => props.$isUnread ? '700' : '500'};
  
  ${props => props.$isUnread && `
    color: #0f5f6b;
  `}
`;

const Timestamp = styled.div`
  color: #178B96;
  font-size: 0.875rem;
  font-weight: 500;
`;

const Duration = styled.div`
  color: #178B96;
  font-size: 0.875rem;
  font-weight: 600;
  background: rgba(23, 139, 150, 0.1);
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
`;



const TableHeader = styled.div`
  background: #178B96;
  color: white;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TableTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FilterLabel = styled.span`
  font-size: 0.9rem;
  opacity: 0.9;
`;

const FilterSelect = styled.select`
  background: white;
  color: #00323B;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  
  &:focus {
    outline: 2px solid rgba(255,255,255,0.3);
  }
`;



const PlayButton = styled.button`
  background: #FF7234;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  padding: 0;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    background: #e55a2b;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(255, 114, 52, 0.3);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
  }
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: #dc3545;
  
  p {
    margin-bottom: 20px;
    font-size: 1.1rem;
  }
`;

const RetryButton = styled.button`
  background-color: #178B96;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background-color: #106271;
  }
`;

const AudioPlayerBar = styled.div`
  display: ${props => (props.$visible ? 'flex' : 'none')};
  align-items: center;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const InlineAudio = styled.audio`
  width: 100%;
  min-height: 40px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 24px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const TabButton = styled.button`
  flex: 1;
  padding: 16px 24px;
  border: none;
  background: ${props => props.$active ? '#2DB3C0' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#6b7280'};
  font-weight: ${props => props.$active ? '700' : '500'};
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover:not(:disabled) {
    background: ${props => props.$active ? '#26a0ac' : 'rgba(45, 179, 192, 0.1)'};
    color: ${props => props.$active ? 'white' : '#2DB3C0'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 20%;
    height: 60%;
    width: 1px;
    background: rgba(0, 0, 0, 0.1);
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
  font-family: "Futura PT";
  font-size: 16px;
  padding: 20px 0;
  align-items: center;
`;

const PaginationText = styled.span`
  color: #000;
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 450;
  line-height: normal;
  letter-spacing: 4px;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const PaginationButton = styled.button`
  background-color: transparent;
  border: none;
  color: ${props => props.$disabled ? "#EC3310" : "#106271"};
  font-size: 16px;
  font-family: "Futura PT";
  cursor: ${props => props.$disabled ? "not-allowed" : "pointer"};
  
  &:hover:not(:disabled) {
    opacity: 0.8;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const PageButton = styled.button`
  padding: 0.5rem;
  min-width: 2rem;
  border: 1px solid #E5E7EB;
  background: ${props => props.$active ? '#2DB3C0' : 'white'};
  color: ${props => props.$active ? 'white' : '#374151'};
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    background: ${props => props.$active ? '#2DB3C0' : '#F3F9FA'};
    border-color: ${props => props.$active ? '#2DB3C0' : '#D1D5DB'};
  }
`;

const PageEllipsis = styled.span`
  color: #6B7280;
  padding: 0.5rem;
  font-size: 14px;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.$hasCategory ? 'rgba(45, 179, 192, 0.15)' : 'rgba(107, 114, 128, 0.15)'};
  color: ${props => props.$hasCategory ? '#178B96' : '#6b7280'};
  border: 1px solid ${props => props.$hasCategory ? 'rgba(45, 179, 192, 0.3)' : 'rgba(107, 114, 128, 0.3)'};
  cursor: ${props => props.$editable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  
  ${props => props.$editable && `
    &:hover {
      background: ${props.$hasCategory ? 'rgba(45, 179, 192, 0.25)' : 'rgba(107, 114, 128, 0.25)'};
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  `}
`;

const CategoryEditContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CategoryEditSelect = styled.select`
  padding: 4px 8px;
  border: 1px solid #2DB3C0;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #374151;
  background: white;
  cursor: pointer;
  min-width: 150px;
  flex: 1;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #178B96;
    box-shadow: 0 0 0 2px rgba(45, 179, 192, 0.2);
  }
`;

const CategoryActionButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  ${props => props.$accept ? `
    background: #10b981;
    color: white;
    
    &:hover {
      background: #059669;
      transform: scale(1.1);
    }
  ` : `
    background: #ef4444;
    color: white;
    
    &:hover {
      background: #dc2626;
      transform: scale(1.1);
    }
  `}
  
  &:active {
    transform: scale(0.95);
  }
`;

const DeleteButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  padding: 0;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #dc2626;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;


export default VoicemailDashboard;
